import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    Output,
    ViewChild
} from "@angular/core";
import { WidgetChartService } from "../api/widget-chart.service";
import {
    ChartSettings,
    ChartTooltipFunction,
    ChartTypeEnum,
    ColumnPurpose,
    IChartViewDTO,
    IChartBuildColumnDTO,
    IChartPdfTemplateSettings,
} from "@main/reporting.v3/widget.chart/widget-chart.models";
import {
    IEmittedVariable,
    IQueryVariables,
    IStaticVariable,
    IVariableRule
} from "../../core/variables/variable-models";
import { WidgetChartPdfExportingService } from "@main/reporting.v3/widget.chart/api/widget-chart-pdf-exporting.service";
import { WidgetChartAuxiliaryService } from "@main/reporting.v3/widget.chart/api/widget-chart-auxiliary.service";
import { PdfExportSettings, PdfExportSource, WidgetOutputMode } from "@main/reporting.v3/core/reporting-models";
import { JsReportConfiguration } from "@main/pdf-exporting/js-report/js-report.models";
import { IWidgetComponent } from "@main/reporting.v3/core/widgets/widget-component";
import { IPdfExportable } from "@main/reporting.v3/core/exporting/pdf-exportable";
import { VariableRuleService } from "../../core/variables/variable-rule.service";
import { VariableHubService } from "../../core/variables/variable-hub.service";
import { IVariableReceiver } from "../../core/variables/variable-receiver";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { ChartData, ChartDataset } from "chart.js";
import { MessageService } from "primeng/api";
import { UIChart } from "primeng/chart";
import { Message } from "@bbwt/classes";
import { v4 as uuidv4 } from "uuid";


@Component({
    selector: "widget-chart",
    templateUrl: "./widget-chart.component.html",
    styleUrls: ["./widget-chart.component.scss"]
})
export class WidgetChartComponent implements IWidgetComponent, IVariableReceiver, IPdfExportable, OnDestroy {
    // Widget settings.
    public readonly widgetType: string = "chart";
    public readonly widgetSelector: string = "widget-chart";

    // Variable handling settings.
    public readonly variableReceiverId: string = uuidv4();

    // Chart settings.
    protected chartData: ChartData;
    protected dataGroups: any[][] = [];
    protected chartDatasets: ChartDataset[];
    protected chartSettings: ChartSettings;

    // Chart columns configurations.
    protected axisXQueryAlias: string;
    protected axisYQueryAlias: string;
    protected seriesQueryAlias: string;
    protected bubbleSizeQueryAlias: string;
    protected columnsOnTooltip: IChartBuildColumnDTO[] = [];

    // Dataset dialog settings.
    protected selectedDatasets: ChartDataset[] = [];
    protected datasetManagerVisible: boolean = false;

    // Pdf view settings.
    protected pdfViewChartWidth: string;
    protected pdfViewChartHeight: string;
    protected renderPdfViewHidden: boolean;

    // Loading settings.
    public pdfExportingLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);

    // Component fields.
    public widgetTitle: string;

    private variables: IEmittedVariable[];
    private queryVariables: IEmittedVariable[];

    private _dataRows: any[] = [];
    private _pdfViewChart: UIChart;
    private _widgetSourceId: string;
    private _widgetVisible: boolean;
    private _chartView: IChartViewDTO;
    private _staticVariables: IStaticVariable[];

    // Shows or hides exporting button.
    @Input() exportable: boolean = true;

    // Determines which HTML code should be used for rendering: web view of pdf exporting view.
    @Input() output: WidgetOutputMode = "web";

    // Flag to ignore widget visibility rules (for preview purposes).
    @Input() ignoreDisplayRule: boolean;

    // Emitter to notify that PDF view is ready for exporting.
    @Output() readonly pdfExportingReady: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild(UIChart) private chart: UIChart;

    constructor(private widgetChartPdfExportingService: WidgetChartPdfExportingService,
                private widgetChartAuxiliaryService: WidgetChartAuxiliaryService,
                private widgetChartService: WidgetChartService,
                private variableRuleService: VariableRuleService,
                private variableHubService: VariableHubService,
                private messageService: MessageService,
                private elementRef: ElementRef) {
    }

    // Captures PDF view container and notify when it is ready.
    @ViewChild("pdfViewChart") protected set pdfViewChart(value: UIChart) {
        if (!!value) {
            // Wait a little time for the chart to render.
            setTimeout((): void => {
                this._pdfViewChart = value;
                this.pdfExportingReady.emit();
            }, 100);
        } else this._pdfViewChart = null;
    }

    protected get pdfViewChart(): UIChart {
        return this._pdfViewChart;
    }

    // Determines if the chart is able to receive variables.
    @Input() set variableReceiver(value: boolean) {
        if (!!value || value == null) this.variableHubService.registerVariableReceiver(this);
        else this.variableHubService.unregisterVariableReceiver(this);
    }

    // Set of fixed-value variables to consider during data fetching.
    @Input() set staticVariables(value: IStaticVariable[]) {
        if (!value) return;
        this._staticVariables = value;
        this.refreshWidgetView();
    }

    get staticVariables(): IStaticVariable[] {
        return this._staticVariables;
    }

    // Base data of the chart. Refresh chart data every time this variable changes.
    @Input() set dataRows(value: any[]) {
        if (!value) return;
        this._dataRows = value;
        this.refreshChartData();
    }

    get dataRows(): any[] {
        return this._dataRows;
    }

    // View of the chart. Refresh chart settings every time this variable changes.
    @Input() set chartView(value: IChartViewDTO) {
        if (!value) return;
        this._chartView = value;
        this._widgetSourceId = value.widgetSourceId;
        this.refreshWidgetView(true);
    };

    get chartView(): IChartViewDTO {
        return this._chartView;
    }

    // Widget source of the chart. Refresh all widget every time this variable changes.
    @Input() set widgetSourceId(value: string) {
        if (this.widgetSourceId === value) return;
        this._widgetSourceId = value;
        this.refreshWidget().then();
    }

    get widgetSourceId(): string {
        return this._widgetSourceId;
    }

    @Input() set widgetVisible(value: boolean) {
        this._widgetVisible = value;
    }

    get widgetVisible(): boolean {
        return this.ignoreDisplayRule || this._widgetVisible;
    }

    get widgetName(): string {
        return this.chartView?.widgetSource?.name;
    }

    get widgetCode(): string {
        return this.chartView?.widgetSource?.code;
    }

    get pdfViewVisible(): boolean {
        return this.widgetVisible;
    }

    get hasFixedSize(): boolean {
        if (!this.chart) return false;

        return this.widgetChartAuxiliaryService.isFixedWidth(this.chart.width)
            && this.widgetChartAuxiliaryService.isFixedHeight(this.chart.width);
    }

    get chartGraphClass(): string {
        return this.widgetChartAuxiliaryService.chartGraphClass;
    }

    get widgetContainerClass(): string {
        return this.widgetChartAuxiliaryService.widgetContainerClass;
    }


    @HostListener("window:resize") protected onResize(): void {
        // If the chart has fixed size, and it should be responsive,
        // refresh the chart on each resize, to avoid it to become blurry.
        if (this.hasFixedSize && this.chart?.options?.responsive) this.chart.refresh();
    }


    public ngOnDestroy(): void {
        this.variableHubService.unregisterVariableReceiver(this);
        this.pdfExportingLoading.complete();
    }


    // Variable management methods.
    public receiveEmittedVariables(variables: IEmittedVariable[]): void {
        // Save the current state of variables.
        const oldVariables: IEmittedVariable[] = this.variables ?? [];
        this.variables = variables;

        // If there is no widget yet, take all variables as last emitted variables.
        // Otherwise, take as last emitted variables only the variables related to this widget.
        const oldQueryVariables: IEmittedVariable[] = this.queryVariables ?? [];
        this.queryVariables = this.variableRuleService.filterVariableSet(this.chartView?.queryVariables, variables);

        if (!this.chartView) return;

        // Show/hide widget if required.
        const displayRule: IVariableRule = this.chartView.widgetSource?.displayRule;

        this.widgetVisible = !displayRule || this.variableRuleService.containsMatch(displayRule, variables);

        if (!this.variableRuleService.equalVariableSets(this.variables, oldVariables)) this.refreshTitle();

        // Refresh the widget if some related variable changed its value.
        if (!this.variableRuleService.equalVariableSets(this.queryVariables, oldQueryVariables)) this.reloadChart().then();

    }

    // Refreshing methods.
    private async refreshWidget(): Promise<void> {
        if (!this.widgetSourceId) return;

        this.chartView = await this.widgetChartService.getView(this.widgetSourceId);

        await this.requestData();
    }

    private refreshChartData(): void {
        if (!this.dataRows) return;

        this.dataGroups = this.widgetChartAuxiliaryService.groupBy(this.dataRows, this.seriesQueryAlias);

        const dataSets: ChartDataset[] = this.dataGroups.map((group, i) => {
            // Set dataset label as the series column value of the first entry in the group.
            const label: string = group[0]?.[this.seriesQueryAlias] ?? "No data";

            // Set data as the y-axis column value of each element in the group.
            const data: number[] = this.widgetChartAuxiliaryService.sortBy(group, this.axisXQueryAlias)
                .map(data => data[this.axisYQueryAlias]);

            // Set radius only if required (chart is bubble chart and bubble size alias is set).
            let radius: number[];
            if (!!this.bubbleSizeQueryAlias && !!group.length) {
                // Set radius as the bubble size column value of each element in the group.
                radius = group.map(data => data[this.bubbleSizeQueryAlias]);
                // Calculate a constant to normalize bubble sizes due respect the number of bubbles.
                const max = Math.max(...radius);
                const constant = 5000 / radius.length;
                // Use the constant to normalize the radius.
                radius = radius.map(data => (data * constant) / max);
            }

            // Find the old version of this dataset.
            const oldDataset: ChartDataset = this.chartDatasets?.find(dataset => dataset.label === label);
            // Set hidden status as the status of the old dataset, if exists.
            const hidden: boolean = !!oldDataset ? oldDataset.hidden : i > 10;

            return <ChartDataset>{
                label: label,
                index: i,
                data: data,
                radius: radius,
                hidden: hidden
            }
        });

        // Update data, declaring a dataset foreach group.
        this.chartDatasets = this.widgetChartAuxiliaryService.sortBy(dataSets, "label");

        // Set datasets as the visible datasets of the chart (to avoid loading issues).
        const datasets: ChartDataset[] = this.chartDatasets.filter(dataset => !dataset.hidden);

        // Set dataset labels as the x-axis column value of each element in the first group.
        const dataGroup: any[] = this.widgetChartAuxiliaryService.sortBy(this.dataGroups[0], this.axisXQueryAlias);
        const labels: string[] = this.widgetChartAuxiliaryService.toStandardString(dataGroup.map(data => data[this.axisXQueryAlias]));

        this.chartData = { labels: labels, datasets: datasets };
    }

    private refreshTitle(): void {
        if (!this.chartView?.widgetSource?.title) return;
        let processedTitle = this.variableRuleService.embedVariableValues(this.chartView.widgetSource?.title, this.variables);
        this.widgetTitle = this.variableRuleService.cleanupUnmatchedVariables(processedTitle);
    }

    private refreshChartSettings(): void {
        if (!this.chartView) return;

        const oldSeriesQueryAlias: string = this.seriesQueryAlias;

        this.seriesQueryAlias = this.chartView.columns
            .find(column => column.columnPurpose === ColumnPurpose.Series)?.queryAlias;
        this.axisXQueryAlias = this.chartView.columns
            .find(column => column.columnPurpose === ColumnPurpose.AxisX)?.queryAlias;
        this.axisYQueryAlias = this.chartView.columns
            .find(column => column.columnPurpose === ColumnPurpose.AxisY)?.queryAlias;
        this.bubbleSizeQueryAlias = this.chartView.columns
            .find(column => column.columnPurpose === ColumnPurpose.BubbleSize)?.queryAlias;
        this.columnsOnTooltip = this.chartView.columns
            .filter(column => column.columnPurpose === ColumnPurpose.Tooltip);

        if (this.seriesQueryAlias !== oldSeriesQueryAlias) this.chartDatasets = null;

        let settings: ChartSettings = null;

        try {
            if (!!this.chartView.chartSettingsJson) settings = JSON.parse(this.chartView.chartSettingsJson);
        } catch (error) {
            this.messageService.add(Message.Warning(error.message, "Invalid chart settings object"));
        }

        if (!settings) settings = { type: ChartTypeEnum.Line, width: "500px", height: "500px" };
        if (!settings.options) settings.options = { aspectRatio: 0.8, maintainAspectRatio: false };
        if (!settings.options.plugins) settings.options.plugins = {};
        if (!settings.options.plugins.tooltip) settings.options.plugins.tooltip = {};
        if (!settings.options.animation) settings.options.animation = { duration: 0 };

        settings.options.plugins.tooltip = {
            ...settings.options.plugins.tooltip,
            enabled: !!this.columnsOnTooltip.length,
            external: this.getTooltipFunction()
        };

        settings.plugins = [this.widgetChartAuxiliaryService.getBackgroundPlugin()];

        this.chartSettings = settings;

        this.pdfViewChartWidth = this.widgetChartAuxiliaryService.convertRelativeWidthToPx(this.chartSettings.width);
        this.pdfViewChartHeight = this.widgetChartAuxiliaryService.convertRelativeHeightToPx(this.chartSettings.height);

        if (this.widgetVisible != null) return;
        this.widgetVisible = this.ignoreDisplayRule || !this.chartView.widgetSource?.displayRule;
    }

    private refreshWidgetView(complete: boolean = false): void {
        if (complete) {
            this.refreshChartSettings();
        } else {
            void this.reloadChart();
        }
        this.refreshTitle();
    }

    private async requestData(): Promise<void> {
        // Consider current status of the variables in data fetching.
        const queryVariables: IQueryVariables = { variables: this.getWidgetVariables() };
        this.dataRows = await this.widgetChartService.getQueryDataRows(this.chartView.querySourceId, queryVariables);
    }

    private async reloadChart(): Promise<void> {
        if (!this.chartView) return;

        // Fetch chart query data for the new set of variables. Refresh chart data.
        const queryVariables: IQueryVariables = { variables: this.getWidgetVariables() };

        this.dataRows = await this.widgetChartService.getQueryDataRows(this.chartView.querySourceId, queryVariables);
    }

    // Dataset manager methods.
    protected showDatasetManager(): void {
        if (!this.chartData) return;
        this.selectedDatasets = [...this.chartData.datasets];
        this.datasetManagerVisible = true;
    }

    protected changeDatasetsVisibility(): void {
        if (!this.chartData) return;
        this.chartDatasets.forEach(dataset => dataset.hidden = true);
        this.selectedDatasets.forEach(dataset => dataset.hidden = false);
        this.chartData.datasets = this.widgetChartAuxiliaryService.sortBy(this.selectedDatasets, "label");
        this.hideDatasetManager();
        this.chart.refresh();
    }

    protected hideDatasetManager(): void {
        this.datasetManagerVisible = false;
        this.selectedDatasets = [];
    }

    // Auxiliary methods.
    private getWidgetVariables(): IEmittedVariable[] {
        const staticVariables: IEmittedVariable[] = this.staticVariables?.map(variable =>
            <IEmittedVariable>{ ...variable, empty: false, behaviorOnEmpty: "populate" }) ?? [];

        return staticVariables.concat(this.queryVariables ?? []);
    }

    private getTooltipFunction(): ChartTooltipFunction {
        return (context) => {
            const { tooltip } = context;

            if (!tooltip.$animations || !this.columnsOnTooltip?.length) return;

            const dataIndex: number = tooltip.dataPoints[0].dataIndex;
            const datasetIndex: number = tooltip.dataPoints[0].dataset["index"];
            const data = this.dataGroups[datasetIndex][dataIndex];

            const columns: string[] = this.columnsOnTooltip.map(column =>
                `${column.chartAlias}: ${data[column.queryAlias]}`.slice(0, 50));

            tooltip.height = 30 + 17.5 * columns.length;
            tooltip.width = 8 * Math.max(...columns.map(data => data.length));
            tooltip.$animations.height.update(tooltip.$animations.height, tooltip.height, null);
            tooltip.$animations.width.update(tooltip.$animations.width, tooltip.width, null);

            tooltip.title = [data[this.seriesQueryAlias] ?? ""];

            tooltip.body = columns.map(data => <any>{ after: [], before: [], lines: [data] });

            tooltip.labelTextColors = columns.map(_ => tooltip.labelTextColors[0]);
            tooltip.labelColors = columns.map(_ => tooltip.labelColors[0]);
            tooltip.labelPointStyles = columns.map(_ => tooltip.labelPointStyles[0]);
        }
    }

    // PDF exporting methods.
    private async renderPdfViewForFunction<T>(func: () => Promise<T>): Promise<T> {
        if (this.pdfViewChart) return func();

        // Render PDF view hidden, and then execute parameter function.
        this.renderPdfViewHidden = true;
        await firstValueFrom(this.pdfExportingReady);

        return func().finally(() => this.renderPdfViewHidden = false);
    }

    private getChartPdfTemplateSettings(): IChartPdfTemplateSettings {
        return {
            template: this.elementRef?.nativeElement?.innerHTML,
            chart: this.pdfViewChart
        };
    }

    public async getPdfConfiguration(): Promise<JsReportConfiguration> {
        if (!this.pdfViewChart) return this.renderPdfViewForFunction(this.getPdfConfiguration.bind(this));

        const settings: IChartPdfTemplateSettings = this.getChartPdfTemplateSettings();
        return await this.widgetChartPdfExportingService.getPdfConfiguration(settings);
    }

    public async generatePdf(): Promise<void> {
        if (this.pdfExportingLoading.value) return;

        try {
            this.pdfExportingLoading.next(true);

            const configuration: JsReportConfiguration = await this.getPdfConfiguration();

            const settings: PdfExportSettings = {
                source: PdfExportSource.WidgetChart,
                configuration: configuration,
                fileName: this.widgetName
            };

            await this.widgetChartPdfExportingService.requestPdfExport(this.widgetSourceId, settings);

        } finally {
            this.pdfExportingLoading.next(false);
        }
    }
}